<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import SharedEntityDataTable from "src/components/shared/SharedEntityDataTable.vue";
import type { DataTableFilterType } from "src/types/data-table";
import type { EntitySchema } from "src/types/entity-schema";
import { useLocalStorage } from "@vueuse/core";
import type { CrudRouteMeta } from "src/router/types";

const route = useRoute();
const routeMeta = route.meta as CrudRouteMeta;
const routeName = computed(() => route.name?.toString());
const entitySchema: EntitySchema = {
  entity: routeName.value ?? "",
  ...routeMeta,
};
const filterType = ref<DataTableFilterType>("simple");

const columnOptions = computed(() => {
  if (routeMeta.tableColumnsDefault) {
    return routeMeta.tableColumnsDefault
      .filter((col) => col.name !== "actions")
      .map((col) => ({
        header: col.name,
        field: col.name,
      }));
  }
  return Object.keys(routeMeta.tableColumns)
    .filter((column) => column !== "actions")
    .map((column) => ({
      header: column,
      field: column,
    }));
});

const selectedColumns = useLocalStorage<{ header: string; field: string }[]>(
  `crud-${routeName.value}-selected-columns`,
  [...columnOptions.value]
);

const filteredTableColumnsDefault = computed(() => {
  return selectedColumns.value.map((col) => ({ name: col.field }));
});

function onToggleSelectedColumns(columns: { header: string; field: string }[]) {
  selectedColumns.value = columns;
}
</script>

<template>
  <SharedEntityDataTable
    v-model:filter-type="filterType"
    :schema="entitySchema"
    :filters="routeMeta.tableFilters"
    :table-columns="routeMeta.tableColumns"
    :table-columns-default="filteredTableColumnsDefault"
  >
    <template #header-left>
      <MultiSelect
        :modelValue="selectedColumns"
        :options="columnOptions"
        optionLabel="header"
        @update:modelValue="onToggleSelectedColumns"
        placeholder="Select Columns"
        :max-selected-labels="3"
      />
    </template>
  </SharedEntityDataTable>
</template>
